import * as Sentry from '@sentry/nuxt'
import type { ErrorEvent, EventHint } from '@sentry/core'

const config = useRuntimeConfig()

// Skip Sentry initialization if SENTRY_DISABLED is set to any value
if (config.public.sentry?.disabled) {
  // Sentry is disabled via SENTRY_DISABLED environment variable
} else if (
  config.public.sentry?.dsn &&
  (process.env.NODE_ENV === 'production' ||
    process.env.NODE_ENV === 'development')
) {
  Sentry.init({
    debug: config.public.sentry?.debug || false,
    dsn: config.public.sentry.dsn,
    tunnel: '/api/sentry-tunnel',
    environment:
      config.public.sentry.config?.environment ||
      process.env.NODE_ENV ||
      'development',
    release: config.public.alfredVersion?.replace('-', '.') || 'noversion',
    sendDefaultPii: true, // Enable IP address collection
    tracesSampleRate: 0, // DISABLED - only error tracking, no performance tracing
    integrations: function (integrations) {
      // integrations will be all default integrations

      const toAdd = [
        // REMOVED: zodErrorsIntegration - never seen in error reports
        // REMOVED: reportingObserverIntegration - adds spam, ends up ignored
        // REMOVED: piniaIntegration - stateTransformer causes 731ms GC pressure
        // REMOVED: moduleMetadataIntegration - info already in source code
        // DISABLED: tracesSampleRate=0 - no performance tracing, errors only
        Sentry.vueIntegration({
          // Basic Vue error handling only - no component tracking
        }),
        // Additional integrations for better error context:
        Sentry.extraErrorDataIntegration({
          // Captures all non-native error properties as extra data
          depth: 5, // How deep to traverse the error object
        }),
        Sentry.httpClientIntegration({
          // Captures failed HTTP requests with request/response info
          // 414 (URI Too Long) is included because server islands encode props
          // in the URL — large props can exceed Nginx's URI limit
          failedRequestStatusCodes: [414, [500, 599]],
          failedRequestTargets: [/.*/],
        }),
        Sentry.contextLinesIntegration({
          // Adds source code context to stack traces (client-side)
          frameContextLines: 7, // Number of lines before/after the error line
        }),
        // feedbackIntegration lazy-loaded on demand via loadSentryFeedback()
        // Saves ~37KB from initial bundle — only loaded on AI search + error pages
      ]
      integrations.push(...toAdd)

      // REMOVED: BrowserApiErrors — wraps setTimeout/addEventListener/etc with
      // try-catch for better stack traces, but adds ~1% CPU per interaction via
      // sentryWrapped overhead. With tracesSampleRate=0 the metadata it collects
      // isn't used. Unhandled errors are still captured by globalHandlersIntegration
      // and Vue errors by vueIntegration above.
      return integrations.filter(function (integration) {
        return (
          integration.name !== 'Dedupe' &&
          integration.name !== 'BrowserApiErrors'
        )
      })
    },
    normalizeDepth: 10,
    ignoreErrors: [
      // Third-party noise
      /GSI_LOGGER/,
      /cookieyes/,
      /google_tag_manager is not defined/,
      // Browser deprecation warnings
      /Unload event listeners are deprecated/,
      /Attribution Reporting is deprecated/,
      /Topics API is deprecated/,
      // Abort errors (navigation cancellation, user-triggered cancellations)
      /AbortError|aborted|Fetch is aborted/i,
      // Chunk loading failures (network blips, deployment race conditions)
      /Loading chunk/,
      /ChunkLoadError/,
      /Loading CSS chunk/,
      /Failed to (fetch dynamically imported|import dynamically)/,
      /Importing a module script failed/,
      /Script loading timed out/,
      // Browser security restrictions
      /Blocked a frame with origin/,
      /The operation is insecure/,
      // Benign browser warning — spec-defined, not actionable (25+ ungrouped issues, 9k+ events)
      /ResizeObserver loop/,
      // DuckDuckGo Mobile calls Feature Policy API with unsupported names on iOS WebKit
      /feature named .* was not found/,
    ],
    beforeSend(event: ErrorEvent, _hint: EventHint) {
      // Allow programmatic suppression of Sentry logging
      if (typeof window !== 'undefined' && window.preventSentryLogging) {
        return null
      }

      // Filter generic 403 Forbidden errors (Cloudflare bot blocks, auth edge cases).
      // reportApiError already filters structured API 403s at the call site.
      // This catches unstructured $fetch 403s that bypass reportApiError.
      // Errors tagged with errorCategory (e.g. 'booking') are preserved.
      const errorMessage = event.exception?.values?.[0]?.value || ''
      if (/403\b/.test(errorMessage) && !event.tags?.errorCategory) {
        return null
      }

      /* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Sentry event structure */
      // Filter cookieyes errors by stack frame (ignoreErrors only matches messages, not stack frames)
      const hasCookieyesStack =
        event.exception?.values?.[0]?.stacktrace?.frames?.some((frame: any) =>
          frame.filename?.includes('cookieyes'),
        )
      if (hasCookieyesStack) {
        return null
      }

      // Filter Seznam prohlížeč (SznProhlizec) — Czech browser that injects
      // broken touch handlers causing 20k+ noise events from 46 users.
      // https://pitchup-team.sentry.io/issues/6546073238/
      if (event.contexts?.browser?.name === 'Seznam prohlížeč') {
        return null
      }

      // Enhance non-Error promise rejections with additional context for debugging
      if (errorMessage.includes('Non-Error promise rejection')) {
        event.extra = event.extra || {}
        event.extra.rejectionAnalysis = {
          message: 'Promise rejected with non-Error value',
          recommendation: 'Use reject(new Error(...)) instead of reject()',
          url: event.request?.url || window?.location?.href,
        }
      }
      /* eslint-enable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */

      return event
    },
  })
}
